import type {
  GroupingState,
  RowSelectionState,
  SortingState,
  Updater,
} from "@tanstack/react-table";

import {
  type ColumnDef,
  createColumnHelper,
  getCoreRowModel,
  getExpandedRowModel,
  getGroupedRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useMemo, useState } from "react";

import { Checkbox } from "@/components/checkbox";

import type { FileItem, View } from "../types";

const columnHelper = createColumnHelper<FileItem>();

type UseMyFilesTableProps = {
  data: FileItem[];
  folderPath?: string;
  grouping: GroupingState;
  isMobile: boolean;
  rowSelection: RowSelectionState;
  setGrouping: (grouping: Updater<GroupingState>) => void;
  setRowSelection: (rowSelection: Updater<RowSelectionState>) => void;
  view: View;
};

export function useMyFilesTable({
  data,
  grouping,
  isMobile,
  rowSelection,
  setGrouping,
  setRowSelection,
  view,
}: UseMyFilesTableProps) {
  const [sorting, setSorting] = useState<SortingState>([]);

  const resetSorting = () => {
    setSorting([]);
  };

  const columns = useMemo(() => {
    const defaultColumns = [
      // Selection column
      columnHelper.display({
        cell: ({ row }) =>
          row.original.type === "folder" ? null : (
            <Checkbox
              aria-label={`Select ${row.original.name}`}
              isIndeterminate={row.getIsSomeSelected()}
              isSelected={row.getIsSelected()}
              onChange={row.getToggleSelectedHandler()}
              size="md"
            />
          ),
        enableSorting: false,
        header: ({ table }) => {
          const selectableRows = table
            .getPreFilteredRowModel()
            .rows.filter((row) => row.original.type !== "folder");

          const isAllSelectableRowsSelected =
            selectableRows.length > 0 &&
            selectableRows.every((row) => row.getIsSelected());
          const isSomeSelectableRowsSelected = selectableRows.some((row) =>
            row.getIsSelected(),
          );

          const toggleSelectAllSelectableRows = () => {
            if (isAllSelectableRowsSelected) {
              for (const row of selectableRows) row.toggleSelected(false);
            } else {
              for (const row of selectableRows) row.toggleSelected(true);
            }
          };

          return (
            <Checkbox
              aria-label="Select all non-folder items"
              isIndeterminate={
                isSomeSelectableRowsSelected && !isAllSelectableRowsSelected
              }
              isSelected={isAllSelectableRowsSelected}
              onChange={toggleSelectAllSelectableRows}
              size="md"
            />
          );
        },
        id: "select",
        maxSize: 50,
        size: 50,
      }),
      // Type column (hidden, used for grouping in grid view)
      columnHelper.accessor("type", {
        enableColumnFilter: false,
        enableSorting: true,
        header: "Type",
        id: "type",
      }),
    ] as ColumnDef<FileItem>[];

    if (isMobile) {
      return [
        ...defaultColumns,
        // Name column
        columnHelper.accessor(
          (row) => (
            <div className="flex flex-col gap-2">
              <div>
                {row.name} {row.id}
              </div>
              <div>Last modified: {row.id}</div>
            </div>
          ),
          {
            cell: (info) => info.getValue(),
            enableSorting: true,
            header: "Name",
            id: "mobileName",
            maxSize: undefined, // Allow name column to take all available space
            size: undefined, // Allow name column to take all available space
          },
        ),
      ] as ColumnDef<FileItem>[];
    }

    return [
      ...defaultColumns,
      // Name column
      columnHelper.accessor("name", {
        cell: (info) => info.getValue(),
        enableSorting: true,
        header: "Name",
        maxSize: undefined, // Allow name column to take all available space
        size: undefined, // Allow name column to take all available space
      }),
      // Version column
      columnHelper.accessor("version", {
        cell: (info) => info.getValue(),
        enableSorting: true,
        header: "Version",
      }),
      // Last modified column
      columnHelper.accessor("createdAt", {
        cell: (info) => info.getValue(),
        enableSorting: true,
        header: "Last modified",
        maxSize: 100,
        size: 100,
      }),
      // File type column
      columnHelper.accessor(
        (row) =>
          row.type === "file" ? row.extension?.toUpperCase() || "" : "Folder",
        {
          cell: (info) => info.getValue(),
          header: "File Type",
          id: "fileType",
          maxSize: 100,
          size: 100,
        },
      ),
      // File size column
      columnHelper.accessor("size", {
        cell: (info) => info.getValue(),
        header: "File Size",
        maxSize: 100,
        size: 100,
      }),
    ] as ColumnDef<FileItem>[];
  }, [isMobile]);

  return {
    ...useReactTable({
      columns,
      data,
      enableExpanding: view === "grid",
      enableRowSelection: (row) => row.original.type !== "folder",
      enableSubRowSelection: true,
      getCoreRowModel: getCoreRowModel(),
      getExpandedRowModel: getExpandedRowModel(),
      getGroupedRowModel: getGroupedRowModel(),
      getSortedRowModel: getSortedRowModel(),
      groupedColumnMode: view === "grid" ? "reorder" : false,
      onGroupingChange: setGrouping,
      onRowSelectionChange: setRowSelection,
      onSortingChange: setSorting,
      state: {
        columnVisibility: {
          type: false, // This is a technical column used for grouping in grid view
        },
        grouping,
        rowSelection,
        sorting,
      },
    }),
    resetSorting,
  };
}
